import React from 'react';
import { Grid, Skeleton, Box, Stack, Card, CardContent, useTheme } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const StatBlock = () => (
  <Stack spacing={1} justifyContent="center" height="100%">
    <Skeleton variant="text" width="40%" height={40} />
    <Box>
      <Skeleton variant="text" width="80%" height={30} />
      <Skeleton variant="text" width="30%" height={30} />
    </Box>
  </Stack>
);

const CopyTradingDashboardPageSkeleton = () => {
  const theme = useTheme();

  const GRID_SPACING = { xs: 1, sm: 2 };

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 1.5, md: 2 },
          '&:last-child': { pb: { xs: 1.5, md: 2 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, md: 3 }} ml={{ xs: 0.5, md: 1.5 }}>
        <Skeleton variant="text" width={160} height={60} />
        <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 2 }} />
      </Box>

      <Grid container spacing={GRID_SPACING} mb={{ xs: 2, md: 3 }}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={6} sm={6} md={3} key={`top-${index}`}>
            <SkeletonCard>
              <StatBlock />
            </SkeletonCard>
          </Grid>
        ))}
      </Grid>
      <Stack spacing={{ xs: 2, md: 3 }}>
        {/* 3. Main Table Section */}
        <SkeletonCard sx={{ minHeight: 400 }}>
          <Stack spacing={{ xs: 2, md: 3 }}>
            {/* Table Header: "Trade Copiers" */}
            <Box display="flex" alignItems="center">
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
              <Skeleton variant="text" width={160} height={32} />
            </Box>

            {/* Table Rows (Simulating Master/Slave Hierarchy) */}
            <Stack spacing={2}>
              {/* Header Row */}
              <Skeleton variant="rectangular" height={30} sx={{ borderRadius: 1, opacity: 0.5 }} />

              {/* Row Group 1: Master + Slave */}
              <Box>
                {/* Master Row */}
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5, mb: 1 }} />

                {/* Slave Row (Indented) */}
                <Box
                  sx={{
                    pl: { xs: 1, sm: 4 },
                    borderLeft: `2px solid ${theme.palette.divider}`,
                    ml: { xs: 0.5, sm: 2 }
                  }}
                >
                  <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1.5 }} />
                </Box>
              </Box>

              {/* Row Group 2: Master Only */}
              <Box>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
              </Box>

              {/* Row Group 3: Master Only */}
              <Box>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
              </Box>
            </Stack>
          </Stack>
        </SkeletonCard>
      </Stack>
    </Box>
  );
};

export default CopyTradingDashboardPageSkeleton;
